import { strings } from '../lang';
import { TEMPORARILY_ENABLE_UNIFIED_PRE_SALES_PV } from '../utils/unified-pre-sales-pv';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  CalendarSlot,
  SalesCall1LeadContact,
  SalesCall1LeadContactInfo,
  PreSalesApiResponse,
  PreSalesAutodialerHpInfo,
  PreSalesAdCase,
  PreSalesAvailabilityResult,
  PreSalesBookingChannel,
  PreSalesCancellationReason,
  PreSalesCancellationRequest,
  PreSalesFieldSalesBookSlotRequest,
  PreSalesFieldSalesBookSlotResult,
  PreSalesFieldSalesCalendarSlot,
  PreSalesFieldSalesEligibilityRequest,
  PreSalesFieldSalesSlot,
  PreSalesFormSubmissionRequest,
  PreSalesQuestion,
  PreSalesQualification,
  PreSalesQualificationRequest,
  PreSalesUnifiedSubmissionRequest,
  PreSalesHandoverRequest,
  PreSalesHandoverResult,
  PreSalesSaveFormRequest,
  PreSalesSubmitFormResponse,
  PreSalesZeegBookSlotRequest,
  Result,
  SalesChannel,
  Slot,
} from '../dto/model';

dayjs.extend(isoWeek);

const rootUrl = (process as any).env.REACT_APP_API_ROOT_URL;

export const mapPreSalesFieldSalesSlots = (
  slots: PreSalesFieldSalesSlot[],
  calendarWeek: string,
): PreSalesFieldSalesCalendarSlot[] => {
  const [calendarYear, isoWeekNumber] = calendarWeek.split('-').map(Number);
  const slotsForWeek = slots.filter((slot) => {
    const startDate = dayjs(slot.start);
    return startDate.isoWeekYear() === calendarYear && startDate.isoWeek() === isoWeekNumber && startDate.isoWeekday() !== 7;
  });
  const slotsByStart = slotsForWeek.reduce<Map<string, PreSalesFieldSalesSlot[]>>((groups, slot) => {
    const slotsAtStart = groups.get(slot.start) ?? [];
    slotsAtStart.push(slot);
    groups.set(slot.start, slotsAtStart);
    return groups;
  }, new Map());

  return Array.from(slotsByStart.values())
    .map((slotsAtStart) => ({
      startDate: dayjs(slotsAtStart[0].start),
      endDate: dayjs(slotsAtStart[0].end),
      dailyAvailableCount: slotsAtStart.length,
      salesChannel: SalesChannel.FieldSales,
      fieldSalesSlots: slotsAtStart,
    }))
    .sort((left, right) => left.startDate.valueOf() - right.startDate.valueOf());
};

const preSalesJsonRequest = async <T>(
  url: string,
  init: { method: 'GET' | 'POST' | 'PUT' | 'PATCH'; accessToken: string; body?: unknown; errorMessage: string },
): Promise<PreSalesApiResponse<T>> => {
  try {
    const headers = new Headers({ Authorization: `Bearer ${init.accessToken}` });
    if (init.body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }
    const response = await fetch(url, {
      method: init.method,
      headers,
      ...(init.body !== undefined && { body: JSON.stringify(init.body) }),
    });

    // A non-OK status (e.g. 403 Forbidden) still resolves the fetch, so surface it as a proper,
    // status-aware failure instead of letting response.json() throw into the generic catch below.
    if (!response.ok) {
      let serverError: string | undefined;
      try {
        const problem = await response.json();
        serverError = problem?.error ?? problem?.message;
      } catch {
        // Non-JSON error body (e.g. an HTML error page) — fall back to the status line.
      }
      return {
        success: false,
        error: serverError ?? `${init.errorMessage} (HTTP ${response.status} ${response.statusText})`.trim(),
        httpStatusCode: response.status,
      };
    }

    return await response.json();
  } catch (e: any) {
    return {
      success: false,
      error: e?.message ?? init.errorMessage,
    };
  }
};

export const heatBookingPageService = {
  async updateSalesCall1LeadContact(internalId: string, contact: SalesCall1LeadContact, accessToken: string): Promise<PreSalesApiResponse<SalesCall1LeadContactInfo>> {
    return preSalesJsonRequest<SalesCall1LeadContactInfo>(
      `${rootUrl}/api/v1/presales/ad-cases/${encodeURIComponent(internalId)}/contact?salesChannel=${SalesChannel.SalesCall1Lead}`,
      { method: 'PATCH', accessToken, body: contact, errorMessage: strings.adBookingPage.contactSaveError },
    );
  },
  async openPreSalesAdCase(internalId: string, accessToken: string): Promise<PreSalesApiResponse<PreSalesAdCase>> {
    return preSalesJsonRequest<PreSalesAdCase>(
      `${rootUrl}/api/v1/presales/ad-cases/${encodeURIComponent(internalId)}/open?salesChannel=${SalesChannel.SalesCall1Lead}`,
      { method: 'POST', accessToken, errorMessage: strings.adBookingPage.openCaseError },
    );
  },

  async getPreSalesAdCase(internalId: string, accessToken: string): Promise<PreSalesApiResponse<PreSalesAdCase>> {
    return preSalesJsonRequest<PreSalesAdCase>(
      `${rootUrl}/api/v1/presales/ad-cases/${encodeURIComponent(internalId)}?salesChannel=${SalesChannel.SalesCall1Lead}`,
      { method: 'GET', accessToken, errorMessage: strings.adBookingPage.caseLoadError },
    );
  },

  async getPreSalesQuestionnaire(accessToken: string): Promise<PreSalesApiResponse<PreSalesQuestion[]>> {
    return preSalesJsonRequest<PreSalesQuestion[]>(
      `${rootUrl}/api/v1/presales/unified/questionnaires/Main?withPv=${TEMPORARILY_ENABLE_UNIFIED_PRE_SALES_PV}&salesChannel=${SalesChannel.SalesCall1Lead}`,
      { method: 'GET', accessToken, errorMessage: strings.adBookingPage.questionnaireLoadError },
    );
  },
  async getPreSalesAutodialerHpInfo(crmId: string, accessToken: string): Promise<PreSalesApiResponse<PreSalesAutodialerHpInfo>> {
    return preSalesJsonRequest<PreSalesAutodialerHpInfo>(
      `${rootUrl}/api/v1/presales/deals/${encodeURIComponent(crmId)}/prefill?salesChannel=${SalesChannel.HeatSales}`,
      { method: 'GET', accessToken, errorMessage: 'Failed to load Autodialer HP information' },
    );
  },

  async handoverPreSalesCustomer(request: PreSalesHandoverRequest, accessToken: string): Promise<PreSalesApiResponse<PreSalesHandoverResult>> {
    return preSalesJsonRequest<PreSalesHandoverResult>(`${rootUrl}/api/v1/presales/handover`, {
      method: 'POST',
      accessToken,
      body: request,
      errorMessage: 'Failed to forward the customer to PV',
    });
  },

  async qualifyPreSalesForm(request: PreSalesQualificationRequest, accessToken: string): Promise<PreSalesApiResponse<PreSalesQualification>> {
    return preSalesJsonRequest<PreSalesQualification>(`${rootUrl}/api/v1/presales/forms/qualifications`, {
      method: 'POST',
      accessToken,
      body: request,
      errorMessage: 'Failed to qualify pre-sales form',
    });
  },

  async evaluatePreSalesCancellation(request: PreSalesCancellationRequest, accessToken: string): Promise<PreSalesApiResponse<PreSalesCancellationReason[]>> {
    return preSalesJsonRequest<PreSalesCancellationReason[]>(`${rootUrl}/api/v1/presales/forms/cancellation-evaluations`, {
      method: 'POST',
      accessToken,
      body: request,
      errorMessage: 'Failed to evaluate pre-sales cancellation reasons',
    });
  },

  async savePreSalesForm(request: PreSalesSaveFormRequest, accessToken: string): Promise<PreSalesApiResponse<null>> {
    return preSalesJsonRequest<null>(`${rootUrl}/api/v1/presales/forms/answers`, {
      method: 'PUT',
      accessToken,
      body: request,
      errorMessage: 'Failed to save pre-sales form',
    });
  },

  async submitUnifiedPreSalesForm(request: PreSalesUnifiedSubmissionRequest, accessToken: string): Promise<PreSalesApiResponse<PreSalesSubmitFormResponse>> {
    return preSalesJsonRequest<PreSalesSubmitFormResponse>(`${rootUrl}/api/v1/presales/unified/forms/submissions`, {
      method: 'POST', accessToken, body: request, errorMessage: 'Failed to submit unified pre-sales form',
    });
  },

  async submitPreSalesForm(request: PreSalesFormSubmissionRequest, accessToken: string): Promise<PreSalesApiResponse<PreSalesSubmitFormResponse>> {
    return preSalesJsonRequest<PreSalesSubmitFormResponse>(`${rootUrl}/api/v1/presales/forms/submissions`, {
      method: 'POST',
      accessToken,
      body: request,
      errorMessage: 'Failed to submit pre-sales form',
    });
  },

  async updatePreSalesFieldSalesEligibility(
    request: PreSalesFieldSalesEligibilityRequest,
    accessToken: string,
  ): Promise<PreSalesApiResponse<string>> {
    return preSalesJsonRequest<string>(`${rootUrl}/api/v1/presales/field-sales/eligibility`, {
      method: 'PUT',
      accessToken,
      body: request,
      errorMessage: 'Failed to update field sales eligibility',
    });
  },

  async getPreSalesFieldSalesAvailability(zipCode: string, accessToken: string): Promise<PreSalesApiResponse<PreSalesAvailabilityResult>> {
    return preSalesJsonRequest<PreSalesAvailabilityResult>(
      `${rootUrl}/api/v1/presales/field-sales/availability/${encodeURIComponent(zipCode)}`,
      { method: 'GET', accessToken, errorMessage: 'Failed to load Field Sales availability' },
    );
  },

  async getPreSalesFieldSalesSlots(zipCode: string, accessToken: string): Promise<PreSalesApiResponse<PreSalesFieldSalesSlot[]>> {
    const result = await preSalesJsonRequest<{ slots?: PreSalesFieldSalesSlot[] }>(
      `${rootUrl}/api/v1/presales/field-sales/slots/${encodeURIComponent(zipCode)}`,
      { method: 'GET', accessToken, errorMessage: 'Failed to load Field Sales slots' },
    );
    if (!result.success) {
      return {
        success: false,
        error: result.error ?? result.message ?? 'Failed to load Field Sales slots',
      };
    }

    return {
      success: true,
      data: result.data?.slots ?? [],
    };
  },

  async bookPreSalesFieldSalesSlot(request: PreSalesFieldSalesBookSlotRequest, accessToken: string): Promise<PreSalesApiResponse<PreSalesFieldSalesBookSlotResult>> {
    return preSalesJsonRequest<PreSalesFieldSalesBookSlotResult>(`${rootUrl}/api/v1/presales/field-sales/slot-bookings`, {
      method: 'POST',
      accessToken,
      body: request,
      errorMessage: 'Failed to book Field Sales slot',
    });
  },

  async confirmPreSalesFieldSalesBooking(dealId: string, accessToken: string): Promise<PreSalesApiResponse<string>> {
    return preSalesJsonRequest<string>(
      `${rootUrl}/api/v1/presales/field-sales/bookings/${encodeURIComponent(dealId)}`,
      { method: 'POST', accessToken, errorMessage: 'Failed to confirm Field Sales booking' },
    );
  },

  async getPreSalesZeegSlots(calendarWeek: string, accessToken: string, ownerSlug?: string, eventTypeSlug?: string): Promise<PreSalesApiResponse<CalendarSlot[]>> {
    const query = new URLSearchParams();
    if (ownerSlug) query.set('ownerSlug', ownerSlug);
    if (eventTypeSlug) query.set('eventTypeSlug', eventTypeSlug);
    const queryString = query.toString();
    const url = `${rootUrl}/api/v1/calendar/zeeg/availability/${encodeURIComponent(calendarWeek)}${queryString ? `?${queryString}` : ''}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
        }),
      });

      const result = await response.json();
      if (!result?.isSuccess) {
        return {
          success: false,
          error: result?.errors?.join(', ') ?? 'Failed to load Zeeg slots',
        };
      }

      const calendars = Array.isArray(result.data) ? result.data : [];
      return {
        success: true,
        data: calendars.flatMap((calendar: any) =>
          (calendar.slots ?? []).map((slot: any) => ({
            ...slot,
            startDate: dayjs(slot.startDate),
          })),
        ),
      };
    } catch (e: any) {
      return {
        success: false,
        error: e?.message ?? 'Failed to load Zeeg slots',
      };
    }
  },

  async bookPreSalesZeegSlot(request: PreSalesZeegBookSlotRequest, accessToken: string): Promise<Result<Slot>> {
    const url = `${rootUrl}/api/v1/calendar/zeeg/bookings`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(request),
      });
      return await response.json();
    } catch (e: any) {
      return {
        isSuccess: false,
        errors: [e?.message ?? 'Failed to book Zeeg slot'],
        data: {} as Slot,
      };
    }
  },

  async recordPreSalesBookingChannel(
    identifier: { crmId?: string; signupId?: string },
    channel: PreSalesBookingChannel,
    accessToken: string,
    bookingLane?: string,
  ): Promise<PreSalesApiResponse<string>> {
    // The backend FormIdentifier expects the signup id under `leadId` (crmId stays `crmId`). Without either
    // id the request would carry `leadId: undefined`, which the backend cannot resolve — fail here instead of
    // sending a request that can only be rejected.
    const crmId = identifier.crmId?.trim();
    const signupId = identifier.signupId?.trim();
    if (!crmId && !signupId) {
      throw new Error('Cannot record the booking channel without a crmId or signupId.');
    }

    const bodyIdentifier = crmId ? { crmId } : { leadId: signupId };
    return preSalesJsonRequest<string>(`${rootUrl}/api/v1/presales/bookings/channel`, {
      method: 'PUT',
      accessToken,
      // The lane decides the channel server-side; `channel` is the fallback for bookings made outside a lane.
      body: { identifier: bodyIdentifier, channel, ...(bookingLane && { bookingLane }) },
      errorMessage: 'Failed to confirm Video Sales booking',
    });
  },
};
